import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { AbstractMerchantSupplierComponent } from "app/core/common/abstract.merchant-supplier.component";
import { Transaction } from "app/core/models/transaction";
import { MerchantService } from "app/core/services/merchant.service";
import { NotifMessageService } from "app/core/services/notifmessage.service";
import { PosService } from "app/core/services/pos.service";
import { SupplierService } from "app/core/services/supplier.service";
import { TransactionService } from "app/core/services/transaction.service";
import { NGXLogger } from "ngx-logger";
import { combineLatest, forkJoin, of, ReplaySubject, timer } from "rxjs";
import { map, switchMap, tap } from "rxjs/operators";
import { AuthenticatedRouteGuard } from "security/authenticatedRouteGuard.service";
import { KeycloakService } from "security/keycloak.service";
import { tableEventChange } from "../table-utils/classes/tableChangeEvent";
import { columnType } from "../table-utils/enums/columnType";


export class TransactionData {
  transaction : Transaction;
  merchantName : string;
  supplierName : string;
}

@Component({
  selector: "app-transactions-page",
  templateUrl: "./transactions-page.component.html"
})
export class TransactionsPageComponent extends AbstractMerchantSupplierComponent implements OnInit, OnDestroy {

  dataSubject$: ReplaySubject<any[]> = new ReplaySubject<any[]>();
  totalLength$: ReplaySubject<number> = new ReplaySubject<number>();

  isLoadingResults : boolean = true;

  transactions : Map<string, TransactionData> = new Map<string, TransactionData>();
  statusItems: Array<String> = Array<String>();
  statusMap: Array<{ element: String, traduction: String }> = new Array<{ element: String, traduction: String }>();

  columns: {
    data : {
      title: string;
      type: columnType
    }[],
    clickable : boolean;
    };

  pager : {page : number,
    sort : string,
    size : number
  } = {
    page: 0,
    sort: "lastModifiedDate,DESC",
    size: 20
  };

  criteria : Transaction = new Transaction();

  columnPropertyMapping = {
    "CREATION_DATE" : "createdDate",
    "POS_ID" : "posId",
    "SUPPLIER_NAME" : "supplierId",
    "MERCHANT_NAME" : "merchantId",
    "AMOUNT" : "amount",
    "QUANTITY" : "quantity",
    "STATUS" : "status"
  };

  searchForm : FormGroup = new FormGroup({
    paymentTransactionId : new FormControl(),
    id: new FormControl(),
    from: new FormControl(),
    to: new FormControl(),
    posId: new FormControl(),
    merchantId: new FormControl(),
    supplierId: new FormControl(),
    status: new FormControl()
  })

  constructor(
    private transactionService: TransactionService,
    private notifService: NotifMessageService,
    private translateService: TranslateService,
    protected supplierService: SupplierService,
    protected merchantService: MerchantService,
    protected posService: PosService,
    protected log: NGXLogger,
    private roleGuard: AuthenticatedRouteGuard,
    private keycloakService: KeycloakService,
    public router: Router
  ) {
    super(supplierService, merchantService, posService, log, router);
  }

  ngOnInit() {
    this.columns = {
      data : [
      { title: "CREATION_DATE", type: columnType.date },
      { title: "POS_ID", type: columnType.number },
      { title: "SUPPLIER_NAME", type: columnType.text },
      { title: "MERCHANT_NAME", type: columnType.text },
      { title: "AMOUNT", type: columnType.text },
      { title: "QUANTITY", type: columnType.text },
      { title: "STATUS", type: columnType.chip }
    ],
    clickable : true
    };
    this.getMerchants();
    this.getSuppliers();
    this.initializeStatus();
    this.translateService.onLangChange.subscribe(() => {
      this.initializeStatus();
    });
    this.keycloakService.rolesObservable.subscribe(roles => {
      if (roles.length !== 0) {
        if (this.roleGuard.isEnabled('TRANSACTION_SEARCH')) {
          this.loadData();
        }
      }
    });
  }

  formatTransaction(transaction: Transaction, supplierName : string, merchantName : string) {
    this.transactions.set(transaction.id, {transaction, merchantName, supplierName})
    return {
      id:transaction.id,
      CREATION_DATE: transaction.createdDate,
      POS_ID: transaction.posId ? transaction.posId : "N/A",
      SUPPLIER_NAME: supplierName,
      MERCHANT_NAME: merchantName,
      AMOUNT: [this.getDecimalValue(transaction.amount), transaction.currency].join(" "),
      QUANTITY: [this.getDecimalValue(transaction.quantity), transaction.unitOfMeasure].join(" "),
      STATUS: this.getTxStatus(transaction),
    };
  }

  loadData(isLoadingSearch : boolean = false) : void{
    this.isLoadingResults = true;
    this.transactionService
    .findByCriteria(this.criteria, this.pager)
    .pipe(
      tap(transactions => this.totalLength$.next(transactions.totalElements)),
      switchMap(transactions => {
        if (transactions.content.length !== 0) {
          const transactions$ = transactions.content.map(transaction => {
            const supplierName$ = this.getSupplierName(transaction.supplierId);
            const merchantName$ = this.getMerchantName(transaction.merchantId);
            return forkJoin(([supplierName$, merchantName$])).pipe(
              map(([supplierName, merchantName]) => {
                  return this.formatTransaction(transaction, supplierName, merchantName)
              }
              ));
          });
          return combineLatest(transactions$)
        }
        if (isLoadingSearch) {
          this.notifService.notifyInfo('EMPTY_RESULT');
        }
        return of([]);
      }),
      map(transactions => {
        this.isLoadingResults = false;
        if (transactions === null) {
          return [];
        }
        return transactions;
      }),
    ).subscribe(transactions =>  {
      this.dataSubject$.next(transactions);
    })
  }

  ngOnDestroy() {
    super.closeSubscribtions();
  }

  tableChange(event: tableEventChange) {
    const sortProperty = this.columnPropertyMapping[event.sortEvent.active];
    const sortDirection = event.sortEvent.direction.toUpperCase();
    this.pager = {
      page: event.pageIndex,
      sort: sortDirection !== "" && sortProperty ? [sortProperty, sortDirection].join(",") : "lastModifiedDate,DESC",
      size: event.pageSize
    }
    this.loadData();
  }

  search() : void {
    this.criteria = new Transaction();
    for (let field in this.searchForm.value) {
        if (!this.searchForm.value[field]) {
          delete this.searchForm.value[field];
        } else {
          if (field === "status") {
            let trad = this.statusMap.find(Type => Type.traduction == this.searchForm.value[field]);
            this.criteria['transactionStateHistory[0].transactionState'] = (typeof trad !== "undefined") ? trad.element : this.searchForm.value[field];
          } else if (field === "from" || field === "to") {
            const fieldValue = this.searchForm.value[field];
            const date = new Date(fieldValue);
            date.setSeconds(0, 0);
            this.criteria[field] = date.toISOString();
          } else {
            this.criteria[field] = this.searchForm.value[field]
          }
        }
    }
    if (this.criteria && this.criteria["from"] && this.criteria["to"] && this.criteria["from"] > this.criteria["to"]) {
      this.notifService.notifyErrorWithoutDetail("ERROR_DATE");
      return;
    }
    this.loadData(true);
  }

  detailsModal(transactionId : string): void {
    const transactionData = this.transactions.get(transactionId);
    this.notifService.emitModalWithDetails("TRANSACTION_DETAILS", [transactionData.transaction, transactionData.merchantName, transactionData.supplierName]);
  }

  clearSearch(): void {
    this.searchForm.reset();
    this.criteria = new Transaction();
    this.loadData();
  }

  initializeStatus() : void{
    let array = [];
    this.getTransactionStates().forEach(element =>
      this.translateService.get(element).subscribe((trad: string) => {
        array.push(trad);
        this.statusMap.push({ element: element, traduction: trad });
        array.sort((a: string, b: string) => {
          return this.translateService.instant(a).localeCompare(this.translateService.instant(b))
        });
      }));
    this.statusItems = array;
  }

  getTxStatus(tx: Transaction) {
    let transactionStateHistory = tx.transactionStateHistory;
    if (transactionStateHistory != null && transactionStateHistory != undefined && transactionStateHistory.length > 0) {
        var recentDate = new Date(Math.max.apply(null, transactionStateHistory.map(e => {
        return new Date(e.createdDate);
      })));
      var recentHistory = transactionStateHistory.filter(e => {
        var d = new Date(e.createdDate);
        return d.getTime() == recentDate.getTime();
      })[0];

      return recentHistory.transactionState;
    } else {
      return "N/A";
    }
  }

  download() {
    const pager = {
      page: 0,
      sort: this.pager.sort,
      size: 10000
    };
    this.transactionService.findByCriteria(this.criteria, pager).subscribe(
      items => {
        if (items.totalElements > 10000) {
          this.notifService.notifyErrorWithoutDetail('EXPORT_NOTE');
        }
        this.transactionService.downloadFile(items.content, 'Export_results');
      }
    );
  }
}
